import { GridValidRowModel, gridFilteredSortedRowEntriesSelector } from "@mui/x-data-grid-pro";
import { SlateResponse } from "../store/types";
import { isInLineup } from "../../utils/showdownHandler";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { LineupApiResponse } from "../../preContest/queries/queryTypes";

export const canFavoriteLineup = (filteredAlreadySavedLineups: string[] , lineup: GridValidRowModel, uniquePlayersCount: string, currPlayerExposure: { [key: string]: number }, currStackExposure: { [key: string]: number }, totalLineups: number, selectedSlate: SlateResponse, playerExposureDict: { [key: string]: number}, stackExposureDict: { [key: string]: number}, currentFavoritedLineups: string[], allowDupes: boolean, hasCaptain: boolean) => {
  const currentPlayerExposures = Object.entries(currPlayerExposure);
  const currentStackExposures = Object.entries(currStackExposure);
  const globalPlayerExposure = playerExposureDict['global'] || 100;
  const globalStackExposure = stackExposureDict['global'] || 100;


  if(uniquePlayersCount) {
    // RE: Check if the lineup is unique enough based on the users input, basically the amount of unique players they want in the lineup
    const isUniqueEnough = checkForUniqueness(lineup, parseInt(uniquePlayersCount), filteredAlreadySavedLineups, hasCaptain);
  
    if(!isUniqueEnough) {
      return false
    }
  }

  const playerExposuresToIndexIfAdded = currentPlayerExposures
  .filter(x => isInLineup(lineup.exportableLineup, x[0], selectedSlate.site, selectedSlate.sport, getSuperflexSlotIndex(selectedSlate)));
  const stackExposuresToIndexIfAdded = currentStackExposures
  .filter(x => lineup.stacks.find(y => ((selectedSlate.sport?.toLowerCase() === 'mlb' && y.count > 2) || (selectedSlate.sport?.toLowerCase() === 'nfl' && y.count > 0)) && y.team === x[0]));

  const playerCanBeAddedWithoutGoingOver = playerExposuresToIndexIfAdded
  .filter(x => {
    return (x[1] + 1) > (Math.min(playerExposureDict[x[0]] === undefined ? 100 : playerExposureDict[x[0]], globalPlayerExposure) * totalLineups / 100)
  }).length > 0 // See if any player exposures would be exceeded

  const stackCanBeAddedWithoutGoingOver = stackExposuresToIndexIfAdded
  .filter(x => ((x[1] + 1) > (Math.min(stackExposureDict[x[0]] === undefined ? 100 : stackExposureDict[x[0]], globalStackExposure) * totalLineups / 100))).length > 0  // See if any stack exposures would be exceeded

  if (playerCanBeAddedWithoutGoingOver || stackCanBeAddedWithoutGoingOver || (!allowDupes && currentFavoritedLineups.includes(lineup.lineupHashCode))) { 
    // Don't allow dupes if we specify not to
      return false;
  }
  return true;
}

export const indexExposures = (lineup: GridValidRowModel, currPlayerExposure: { [key: string]: number }, currStackExposure: { [key: string]: number }, selectedSlate: SlateResponse, ) => {
  getLineupPlayerKeys(lineup.exportableLineup as string, selectedSlate).forEach(key => {
    currPlayerExposure[key] = (currPlayerExposure[key] || 0) + 1;
  })
  lineup.stacks.forEach(y => {
    if(
      (selectedSlate.sport?.toLowerCase() === 'mlb' && y.count > 2) || (selectedSlate.sport?.toLowerCase() === 'nfl' && y.count > 0)
    ) {
      return currStackExposure[y.team] = (currStackExposure[y.team] || 0) + 1;
    }
    return;
  });
  return { currPlayerExposure, currStackExposure };
}

export const findLineupsToFavorite = (playerExposureDict: { [key: string]: number}, stackExposureDict: { [key: string]: number}, savedLineups:(LineupApiResponse | GridValidRowModel)[], apiRef: React.MutableRefObject<GridApiCommunity>, selectedSlate: SlateResponse, pageCount: number, uniquePlayersCount?: string, allowDupes: boolean = true) => {
  let currentPlayerExposure = initializeCurrentExposureDict(playerExposureDict);
  let currentStackExposure = initializeCurrentExposureDict(stackExposureDict);
  const alreadySavedLineups = savedLineups?.map(x => x.exportableLineup);
  const data = gridFilteredSortedRowEntriesSelector(apiRef)?.map(x => x.model);

  const filteredAlreadySavedLineups = data?.filter(x => !alreadySavedLineups.includes(x.exportableLineup));
  let lineupsToFavorite: GridValidRowModel[] = [];
  let favoritedLineupHashcodes: string[] = [];
  let favoritedLineupsString: string[] = [];
  filteredAlreadySavedLineups.forEach((lineup: GridValidRowModel) => {
    if (canFavoriteLineup(favoritedLineupsString, lineup, uniquePlayersCount || '', currentPlayerExposure, currentStackExposure, pageCount, selectedSlate, playerExposureDict, stackExposureDict, favoritedLineupHashcodes, allowDupes, selectedSlate.type?.toLowerCase() === 'showdown') && lineupsToFavorite.length < pageCount) {
      lineupsToFavorite.push(lineup);
      const { currPlayerExposure, currStackExposure } = indexExposures(lineup, currentPlayerExposure, currentStackExposure, selectedSlate);
      currentPlayerExposure = currPlayerExposure;
      currentStackExposure = currStackExposure;
      favoritedLineupHashcodes.push(lineup.lineupHashCode);
      favoritedLineupsString.push(lineup.exportableLineup);
    }
  });

  const saveableLineups = [
    ...savedLineups,
    ...lineupsToFavorite,
  ]
  return saveableLineups;
}

const initializeCurrentExposureDict = (exposureDict: { [key: string]: number }) => {
  const keys = Object.keys(exposureDict || {});
  const newExposureDict = {};
  keys.forEach(key => {
    newExposureDict[key] = 0;
  });
  return newExposureDict;
}


const checkForUniqueness = (lineup: GridValidRowModel, uniquePlayerPerLineup: number, currentlySavedLineups: string[], hasCaptain: boolean) => {
  let players = lineup.exportableLineup.split(',');
  if(hasCaptain) {
    // If it's a showdown lineup, we need to account for the captain who is always the first player
    players = players.map((x, i) => i === 0 ? `${x}||CPT` : x);
  }

  const doesAnyOtherLineupInvalidateThisOne = currentlySavedLineups.some((x) => {
    let playersInLineup = x.split(',');
    if(hasCaptain) {
      // If it's a showdown lineup, we need to account for the captain who is always the first player
      playersInLineup = playersInLineup.map((x, i) => i === 0 ? `${x}||CPT` : x);
    }

    const uniquePlayers = players.filter(player => !playersInLineup.includes(player));

    return uniquePlayers.length < uniquePlayerPerLineup;
  })

  if(doesAnyOtherLineupInvalidateThisOne) {
    return false;
  }

  return true;
}

// CFB classic rosters have a superflex slot (S-FLEX on DK, Super FLEX on FD) that can hold a QB, RB, WR or TE.
// DK CFB has no TEs today, but its S-FLEX slot allows them (specialPositionsHelper), so TE is listed for both sites.
// It's a lineup slot, not a player position, so it's found by the slot's index in exportableLineup.
// Boosts and exposure caps set on the S-Flex tab are keyed player||S-FLEX, like CPT/FLEX on showdown.
const superflexSlots = ['S-FLEX', 'SUPER FLEX'];
export const superflexKeySuffix = '||S-FLEX';
export const superflexEligiblePositions = ['QB', 'RB', 'WR', 'TE'];

export const isSuperflexSlot = (position?: string) => superflexSlots.includes(position?.trim().toUpperCase() ?? '');

// Index of the superflex slot in the slate's roster order (lineupRules.positions), or -1 if there isn't one
export const getSuperflexSlotIndex = (selectedSlate?: SlateResponse) => {
  if (selectedSlate?.type?.toUpperCase() !== 'CLASSIC') return -1;
  const positions = selectedSlate?.lineupRules?.positions?.split(',') ?? [];
  return positions.findIndex(position => isSuperflexSlot(position));
}

// Every boost/exposure key the players in a lineup count toward: CPT/FLEX keys on showdown, otherwise the player,
// plus player||S-FLEX for whoever fills the superflex slot
export const getLineupPlayerKeys = (exportableLineup: string, selectedSlate: SlateResponse) => {
  const showdown = selectedSlate?.type?.toUpperCase() === 'SHOWDOWN';
  const superflexIndex = getSuperflexSlotIndex(selectedSlate);
  const keys: string[] = [];
  exportableLineup.split(',').forEach((x, i) => {
    const player = x.trim();
    if (showdown) {
      keys.push(i === 0 ? `${player}||CPT` : `${player}||FLEX`);
      return;
    }
    keys.push(player);
    if (i === superflexIndex) keys.push(player + superflexKeySuffix);
  });
  return keys;
}
